import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import './style/Register.css';

const Register = () => {
    const navigate = useNavigate();
    
    // --- State Management ---
    const [showLoader, setShowLoader] = useState(true);
    
    const [websiteData, setWebsiteData] = useState({
        logo: '',
        name: 'CashBolt Media', 
        tagline: 'Join the elite network'
    });

    const [formData, setFormData] = useState({
        name: '',
        compName: '',
        number: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    // Traffic Source Logic
    const [trafficTags, setTrafficTags] = useState([]);
    const [trafficInput, setTrafficInput] = useState('');
    const [typingTimer, setTypingTimer] = useState(null);

    const [acceptTerms, setAcceptTerms] = useState(false);
    const [errors, setErrors] = useState({});
    const [registerLoading, setRegisterLoading] = useState(false);

    // Forgot Password Modal State
    // const [showForgotModal, setShowForgotModal] = useState(false);
    // const [forgotEmail, setForgotEmail] = useState('');
    // const [forgotLoading, setForgotLoading] = useState(false);

    // --- OTP Verification State ---
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [verifyEmail, setVerifyEmail] = useState('');
    const [otpValues, setOtpValues] = useState(new Array(6).fill(""));
    const [otpLoading, setOtpLoading] = useState(false);

    // Captcha State
    const [captchaOptions, setCaptchaOptions] = useState([]);
    const [targetCaptcha, setTargetCaptcha] = useState(null);
    const [selectedCaptcha, setSelectedCaptcha] = useState(null);

    // --- Captcha Generator ---
    const generateCaptcha = () => {
        const icons = [
            { id: 1, symbol: '🚀', name: 'Rocket' },
            { id: 2, symbol: '💎', name: 'Diamond' },
            { id: 3, symbol: '⚡', name: 'Bolt' },
            { id: 4, symbol: '🛡️', name: 'Shield' },
            { id: 5, symbol: '🔥', name: 'Fire' },
            { id: 6, symbol: '👑', name: 'Crown' }
        ];
        const shuffled = icons.sort(() => 0.5 - Math.random()).slice(0, 4);
        const target = shuffled[Math.floor(Math.random() * shuffled.length)];
        
        setCaptchaOptions(shuffled);
        setTargetCaptcha(target);
        setSelectedCaptcha(null);
    };

    // Initialize Captcha on mount
    useEffect(() => {
        generateCaptcha();
    }, []);

    // --- Traffic Source Input Logic ---
    const handleTrafficChange = (e) => {
        const val = e.target.value;
        setTrafficInput(val);

        if (typingTimer) clearTimeout(typingTimer);

        if (val.trim().length > 0) {
            const newTimer = setTimeout(() => {
                addTrafficTag(val);
            }, 800); 
            setTypingTimer(newTimer);
        }
    };

    const addTrafficTag = (text) => {
        const cleanText = text.trim();
        if (cleanText) {
            setTrafficTags(prev => [...prev, cleanText]);
            setTrafficInput(''); 
        }
    };

    const removeTrafficTag = (indexToRemove) => {
        setTrafficTags(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    // Handle Standard Inputs
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Full Name is required';
        if (!formData.compName.trim()) newErrors.compName = 'Company Name is required';
        if (!formData.number.trim()) {
            newErrors.number = 'Phone Number is required';
        } else if (!/^\d{10,15}$/.test(formData.number)) {
            newErrors.number = 'Invalid phone number';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Min 8 characters required';
        }
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        if (!acceptTerms) {
            newErrors.terms = 'Please accept terms & conditions';
        }
        if (!selectedCaptcha || selectedCaptcha.id !== targetCaptcha.id) {
            newErrors.captcha = 'Please select the correct security icon';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // --- UPDATED: Submit Registration ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (trafficInput.trim()) {
            addTrafficTag(trafficInput);
        }

        if (!validateForm()) {
            const form = document.querySelector('.reg-card-unique');
            form.classList.add('shake-anim-unique');
            setTimeout(() => form.classList.remove('shake-anim-unique'), 500);
            return;
        }

        setRegisterLoading(true);
        const trafficSourceString = [...trafficTags, trafficInput].filter(Boolean).join(', ');

        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'affRegister',
                name: formData.name,
                compName: formData.compName,
                number: formData.number,
                email: formData.email,
                password: formData.password,
                trafficSource: trafficSourceString 
            });

            if (response.data.status === true) {
                // --- NEW LOGIC: Check Verify -> Send OTP -> Show Modal ---
                if (response.data.requireVerify) {
                    const emailToVerify = response.data.email;
                    setVerifyEmail(emailToVerify);
                    
                    // Trigger OTP Send immediately
                    try {
                        await axios.post('https://publisher.cashbolt.in.net/server/', {
                            action: 'sendOtp',
                            email: emailToVerify
                        });
                        toast.success(`OTP sent to ${emailToVerify}`);
                        setShowOtpModal(true);
                    } catch (otpErr) {
                        toast.error("Account created, but failed to send OTP. Try 'Forgot Password'.");
                    }
                    
                    setRegisterLoading(false); 
                    return;
                }
                
                // If no verify required
                toast.success(response.data.message || 'Registration Successful!');
                navigate('/login');
            } else {
                toast.error(response.data.message || 'Registration failed');
                setErrors(prev => ({ ...prev, form: response.data.message }));
                generateCaptcha();
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'Server connection failed.';
            toast.error(msg);
            setErrors(prev => ({ ...prev, form: msg }));
        } finally {
            // Only stop loading if we aren't showing the modal
            if (!showOtpModal) {
                setRegisterLoading(false);
            }
        }
    };

    // --- OTP Input Handlers ---
    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;

        const newOtp = [...otpValues];
        newOtp[index] = element.value;
        setOtpValues(newOtp);

        // Focus next input
        if (element.value && element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleOtpKeyDown = (e, index) => {
        // Handle Backspace
        if (e.key === "Backspace") {
            if (!otpValues[index] && e.target.previousSibling) {
                e.target.previousSibling.focus();
            }
        }
    };

    const handleOtpVerifySubmit = async () => {
        const otpCode = otpValues.join('');
        if (otpCode.length < 6) {
            toast.warning('Please enter the full 6-digit code');
            return;
        }

        setOtpLoading(true);
        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'verifyRegisterOtp', // Assuming this action exists in your backend
                email: verifyEmail,
                otp: otpCode
            });

            if (response.data.status === true) {
                toast.success('Email Verified Successfully!');
                setShowOtpModal(false);
                navigate('/login');
            } else {
                toast.error(response.data.message || 'Invalid OTP');
                setOtpValues(new Array(6).fill("")); // Reset on failure
            }
        } catch (error) {
            toast.error('Verification failed. Please try again.');
        } finally {
            setOtpLoading(false);
        }
    };

    return (
        <div className="register-page-wrapper-unique"> 
            
            {showLoader && (
                <Loader 
                    onDataFetched={(data) => setWebsiteData(data)} 
                    onComplete={() => setShowLoader(false)} 
                />
            )}

            <div className="bg-shape-unique shape-1-unique"></div>
            <div className="bg-shape-unique shape-2-unique"></div>

            <div className="reg-container-unique">
                
                {/* Branding Side */}
                <div className="reg-brand-side-unique">
                    <div className="brand-content-unique">
                        {websiteData.logo && (
                            <img src={websiteData.logo} alt="Logo" className="brand-logo-unique" />
                        )}
                        <h1 className="brand-title-unique animated-title-unique">{websiteData.name}</h1>
                        <p className="brand-tagline-unique">{websiteData.tagline}</p>
                    </div>
                </div>

                {/* Form Side */}
                <div className="reg-form-side-unique">
                    <div className="reg-card-unique">
                        <div className="form-header-unique">
                            <h2>Create Account</h2>
                            <p>Enter your details to access the dashboard</p>
                        </div>

                        <form onSubmit={handleSubmit} className="register-form-unique">
                            
                            {/* Name & Comp */}
                            <div className="form-row-unique">
                                <div className="input-group-unique">
                                    <input 
                                        type="text" name="name" placeholder=" " 
                                        value={formData.name} onChange={handleChange} 
                                        className={errors.name ? 'error-border-unique' : ''}
                                    />
                                    <label>Full Name</label>
                                    {errors.name && <span className="error-msg-unique">{errors.name}</span>}
                                </div>
                                <div className="input-group-unique">
                                    <input 
                                        type="text" name="compName" placeholder=" " 
                                        value={formData.compName} onChange={handleChange}
                                        className={errors.compName ? 'error-border-unique' : ''}
                                    />
                                    <label>Company Name</label>
                                    {errors.compName && <span className="error-msg-unique">{errors.compName}</span>}
                                </div>
                            </div>

                            {/* Email & Phone */}
                            <div className="form-row-unique">
                                <div className="input-group-unique">
                                    <input 
                                        type="email" name="email" placeholder=" " 
                                        value={formData.email} onChange={handleChange}
                                        className={errors.email ? 'error-border-unique' : ''}
                                    />
                                    <label>Email Address</label>
                                    {errors.email && <span className="error-msg-unique">{errors.email}</span>}
                                </div>
                                <div className="input-group-unique">
                                    <input 
                                        type="text" name="number" placeholder=" " 
                                        value={formData.number} onChange={handleChange}
                                        className={errors.number ? 'error-border-unique' : ''}
                                    />
                                    <label>Phone Number</label>
                                    {errors.number && <span className="error-msg-unique">{errors.number}</span>}
                                </div>
                            </div>

                            {/* Traffic Source */}
                            <div className="form-row-unique">
                                <div className="input-group-unique tag-input-container-unique">
                                    <div className="tags-wrapper-unique">
                                        {trafficTags.map((tag, index) => (
                                            <span key={index} className="tag-chip-unique">
                                                {tag}
                                                <i onClick={() => removeTrafficTag(index)}>&times;</i>
                                            </span>
                                        ))}
                                        <input 
                                            type="text" 
                                            placeholder=" " 
                                            value={trafficInput} 
                                            onChange={handleTrafficChange}
                                            className="tag-input-field-unique"
                                        />
                                    </div>
                                    <label className={trafficTags.length > 0 || trafficInput ? 'float-label-active' : ''}>
                                        Traffic Sources (Type & Pause)
                                    </label>
                                </div>
                            </div>

                            {/* Passwords */}
                            <div className="form-row-unique">
                                <div className="input-group-unique">
                                    <input 
                                        type="password" name="password" placeholder=" " 
                                        value={formData.password} onChange={handleChange}
                                        className={errors.password ? 'error-border-unique' : ''}
                                    />
                                    <label>Password</label>
                                    {errors.password && <span className="error-msg-unique">{errors.password}</span>}
                                </div>
                                <div className="input-group-unique">
                                    <input 
                                        type="password" name="confirmPassword" placeholder=" " 
                                        value={formData.confirmPassword} onChange={handleChange}
                                        className={errors.confirmPassword ? 'error-border-unique' : ''}
                                    />
                                    <label>Confirm Password</label>
                                    {errors.confirmPassword && <span className="error-msg-unique">{errors.confirmPassword}</span>}
                                </div>
                            </div>

                            {/* Captcha */}
                            <div className="captcha-container-unique">
                                <p className="captcha-label-unique">
                                    Security Check: Select the <span>{targetCaptcha?.name}</span>
                                </p>
                                <div className="captcha-grid-unique">
                                    {captchaOptions.map((opt) => (
                                        <div 
                                            key={opt.id}
                                            className={`captcha-item-unique ${selectedCaptcha?.id === opt.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedCaptcha(opt)}
                                        >
                                            {opt.symbol}
                                        </div>
                                    ))}
                                </div>
                                {errors.captcha && <div className="error-msg-unique center-unique">{errors.captcha}</div>}
                            </div>

                            {/* Extras */}
                            <div className="form-extras-unique">
                                <label className="checkbox-container-unique">
                                    <input 
                                        type="checkbox" 
                                        checked={acceptTerms}
                                        onChange={(e) => setAcceptTerms(e.target.checked)}
                                    />
                                    <span className="checkmark-unique"></span>
                                    I agree to the Terms & Policy
                                </label>
                            </div>
                            
                            {errors.terms && <div className="error-msg-unique center-unique">{errors.terms}</div>}
                            {errors.form && <div className="error-msg-unique center-unique">{errors.form}</div>}

                            {/* Submit Button */}
                            <button type="submit" className="submit-btn-unique wave-effect-unique" disabled={registerLoading}>
                                {registerLoading ? <span className="btn-loader-unique"></span> : (
                                    <>
                                        <span>REGISTER ACCOUNT</span>
                                        <div className="wave-unique"></div>
                                    </>
                                )}
                            </button>

                            <div className="login-redirect-unique">
                                Already have an account? <span onClick={() => navigate('/login')}>Login Here</span>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Forgot Password Modal */}
            {/* {showForgotModal && (
                <div className="modal-overlay-unique" onClick={() => setShowForgotModal(false)}>
                    <div className="modal-content-unique" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-unique">
                            <h3>Reset Password</h3>
                            <button className="close-modal-unique" onClick={() => setShowForgotModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body-unique">
                            <p>Enter your registered email to receive an OTP.</p>
                            <div className="input-group-unique">
                                <input 
                                    type="email" 
                                    placeholder=" " 
                                    value={forgotEmail}
                                    onChange={(e) => setForgotEmail(e.target.value)}
                                />
                                <label>Your Email</label>
                            </div>
                            <button 
                                className="modal-submit-unique" 
                                onClick={handleForgotSubmit}
                                disabled={forgotLoading}
                            >
                                {forgotLoading ? 'SENDING...' : 'SEND OTP'}
                            </button>
                        </div>
                    </div>
                </div>
            )} */}

            {/* --- OTP VERIFICATION MODAL --- */}
            {showOtpModal && (
                <div className="modal-overlay-unique">
                    <div className="modal-content-unique otp-content-unique">
                        <div className="modal-header-unique">
                            <h3>Verify Email</h3>
                            {/* Disabled close button to force verification or refresh */}
                        </div>
                        <div className="modal-body-unique center-unique">
                            <p style={{marginBottom:'1.5rem'}}>
                                Enter the 6-digit code sent to <br/>
                                <span style={{color: '#fff', fontWeight:'bold'}}>{verifyEmail}</span>,<br />
                                Also check spam folder
                            </p>
                            
                            <div className="otp-input-container">
                                {otpValues.map((data, index) => (
                                    <input
                                        className="otp-box"
                                        type="text"
                                        name="otp"
                                        maxLength="1"
                                        key={index}
                                        value={data}
                                        onChange={(e) => handleOtpChange(e.target, index)}
                                        onKeyDown={(e) => handleOtpKeyDown(e, index)}
                                        onFocus={(e) => e.target.select()}
                                    />
                                ))}
                            </div>

                            <button 
                                className="modal-submit-unique" 
                                onClick={handleOtpVerifySubmit}
                                disabled={otpLoading}
                                style={{marginTop: '2rem'}}
                            >
                                {otpLoading ? 'VERIFYING...' : 'VERIFY & LOGIN'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Register;